import type { ApiResponse } from '@/api/base';

/**
 * Adapts a mutation API call for useSWRMutation that eliminates boilerplate.
 * Automatically handles API response unwrapping and error handling.
 *
 * @example
 * const { trigger, isMutating } = useSWRMutation(
 *   'removeDocument',
 *   adaptMutation(removeDocument),
 *   {
 *     onSuccess: () => console.log('Success!'),
 *     onError: (error) => console.error(error)
 *   }
 * );
 *
 * // Usage: just pass the argument directly
 * await trigger(documentId);
 */
export function adaptMutation<TArg, TReturn>(
  apiCall: (arg: TArg) => Promise<ApiResponse<TReturn>>
) {
  return async (_key: string, { arg }: { arg: TArg }) => {
    const response = await apiCall(arg);
    if (response?.error) throw response.error;
    return response.data as TReturn;
  };
}

/**
 * Adapts a query API call for useSWR/useSWRImmutable that eliminates boilerplate.
 * Automatically handles API response unwrapping and error handling.
 * Expects the key to be an array where the second element is the API argument.
 *
 * @example
 * const { data, isLoading } = useSWRImmutable<ReferralSystem>(
 *   user?.currency ? ['playerReferralSystem', user.currency] : null,
 *   adaptQuery(playerReferralSystem)
 * );
 */
export function adaptQuery<TArg, TReturn>(
  apiCall: (arg: TArg) => Promise<ApiResponse<TReturn>>
) {
  return async (key: [string, TArg]) => {
    const response = await apiCall(key[1]);
    if (response?.error) throw response.error;
    return response.data as TReturn;
  };
}
